import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { Row, Col } from 'antd';

import { useQuery } from '@tanstack/react-query';
import { Field, useFormikContext } from 'formik';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { useViaCepAddress } from 'v2/hooks/useViaCepAddress';
import type { AddressFormKit } from 'v2/interfaces/addressKit';
import kitService from 'v2/services/kit';
import { isValidZipcode } from 'v2/services/viaCep';

import { AutoCompleteFormField } from '../FormFields/AutoCompleteFormField';
import { CheckboxFormField } from '../FormFields/CheckboxFormField';

type FieldOption = {
  label: string;
  value: string;
};

const brStates: FieldOption[] = [
  { label: 'Envio internacional', value: 'EX' },
  { label: 'Acre', value: 'AC' },
  { label: 'Alagoas', value: 'AL' },
  { label: 'Amapá', value: 'AP' },
  { label: 'Amazonas', value: 'AM' },
  { label: 'Bahia', value: 'BA' },
  { label: 'Ceará', value: 'CE' },
  { label: 'Distrito Federal', value: 'DF' },
  { label: 'Espírito Santo', value: 'ES' },
  { label: 'Goiás', value: 'GO' },
  { label: 'Maranhão', value: 'MA' },
  { label: 'Mato Grosso', value: 'MT' },
  { label: 'Mato Grosso do Sul', value: 'MS' },
  { label: 'Minas Gerais', value: 'MG' },
  { label: 'Pará', value: 'PA' },
  { label: 'Paraíba', value: 'PB' },
  { label: 'Paraná', value: 'PR' },
  { label: 'Pernambuco', value: 'PE' },
  { label: 'Piauí', value: 'PI' },
  { label: 'Rio de Janeiro', value: 'RJ' },
  { label: 'Rio Grande do Norte', value: 'RN' },
  { label: 'Rio Grande do Sul', value: 'RS' },
  { label: 'Rondônia', value: 'RO' },
  { label: 'Roraima', value: 'RR' },
  { label: 'Santa Catarina', value: 'SC' },
  { label: 'São Paulo', value: 'SP' },
  { label: 'Sergipe', value: 'SE' },
  { label: 'Tocantins', value: 'TO' }
];

const isInternationalDelivery = (state: string) => state === 'EX';

const normalizeString = (value: string) => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const findCityOption = (localidade: string, cities: FieldOption[]) => {
  const normalizedLocalidade = normalizeString(localidade);

  return cities.find(city => {
    return normalizeString(city.value) === normalizedLocalidade || normalizeString(city.label) === normalizedLocalidade;
  });
};

export function mapCity(city: string, state?: string) {
  if (isInternationalDelivery(state)) return city;

  const renamedCity = city.toLowerCase().replace(/(?:^|\s)\S/g, function (city) {
    return city.toUpperCase();
  });

  return {
    label: renamedCity,
    value: city
  };
}

const Address = () => {
  const { values, setFieldValue } = useFormikContext<AddressFormKit>();
  const initialZipcodeRef = useRef(values.zipcode);
  const lastProcessedZipcodeRef = useRef<string | null>(null);
  const pendingCityRef = useRef<string | null>(null);

  const isViaCepEnabled =
    values.zipcode !== initialZipcodeRef.current &&
    isValidZipcode(values.zipcode) &&
    !isInternationalDelivery(values.state);

  const viaCepQuery = useViaCepAddress(values.zipcode, { enabled: isViaCepEnabled });

  const getCitiesByState = useQuery<FieldOption[]>({
    queryKey: ['states', values.state, 'cities'],
    queryFn: () => kitService.getCitiesByState(values.state).then(result => result.map(mapCity)),
    enabled: !!values.state
  });

  useEffect(() => {
    if (values.noNumber) setFieldValue('number', '');
  }, [setFieldValue, values.noNumber]);

  useEffect(() => {
    if (!viaCepQuery.data || !isViaCepEnabled) {
      return;
    }

    if (lastProcessedZipcodeRef.current === values.zipcode) {
      return;
    }

    const { uf, logradouro, bairro, localidade } = viaCepQuery.data;
    const stateExists = brStates.some(state => state.value === uf);

    if (!stateExists) {
      return;
    }

    lastProcessedZipcodeRef.current = values.zipcode;

    setFieldValue('state', uf);
    setFieldValue('street', logradouro || '');
    setFieldValue('neighborhood', bairro || '');
    setFieldValue('city', null);
    pendingCityRef.current = localidade || null;
  }, [viaCepQuery.data, isViaCepEnabled, values.zipcode, setFieldValue]);

  useEffect(() => {
    if (!viaCepQuery.isError || !isViaCepEnabled) {
      return;
    }

    if (lastProcessedZipcodeRef.current === values.zipcode) {
      return;
    }

    lastProcessedZipcodeRef.current = values.zipcode;
    pendingCityRef.current = null;
    toast.error('Não foi possível localizar o endereço para o CEP informado.');
  }, [viaCepQuery.isError, isViaCepEnabled, values.zipcode]);

  useEffect(() => {
    const pendingCity = pendingCityRef.current;

    if (!pendingCity || !getCitiesByState.data?.length) {
      return;
    }

    const matchedCity = findCityOption(pendingCity, getCitiesByState.data);

    if (matchedCity) {
      setFieldValue('city', matchedCity);
    }

    pendingCityRef.current = null;
  }, [getCitiesByState.data, setFieldValue]);

  return (
    <>
      <Col xs={24} sm={8}>
        <Field
          label={isInternationalDelivery(values.state) ? 'ZIP Code' : 'CEP'}
          name='zipcode'
          id='zipcode'
          mask='zipcode'
          component={TextFormField}
          disabled={viaCepQuery.isFetching && isViaCepEnabled}
        />
      </Col>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Field
            label='Estado'
            name='state'
            id='state'
            options={brStates}
            component={SelectFormField}
            onChange={() => setFieldValue('city', '')}
            placeholder='Selecione um estado'
          />
        </Col>
        <Col xs={24} sm={12}>
          {isInternationalDelivery(values.state) ? (
            <Field
              label='Cidade'
              name='city'
              id='city-text'
              placeholder='Digite o nome da sua cidade'
              component={TextFormField}
              maxLength={40}
            />
          ) : (
            <Field
              label='Cidade'
              name='city'
              id='city-select'
              options={getCitiesByState.data || []}
              component={AutoCompleteFormField}
              isLoading={getCitiesByState.isLoading}
              placeholder={values.state ? 'Selecione uma cidade' : 'Selecione um estado primeiro'}
              disabled={getCitiesByState.isLoading || !values.state}
              emptyOptionLabel='Selecione uma cidade'
              maxLength={40}
            />
          )}
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Field label='Endereço' name='street' id='street' component={TextFormField} />
        </Col>
        <Col xs={24} sm={6}>
          <Field
            label='Número'
            name='number'
            id='number'
            component={TextFormField}
            mask='number'
            disabled={values.noNumber}
          />
        </Col>
        <Col xs={24} sm={6} className='pt-0 sm:pt-6'>
          <Field label='Sem Número' name='noNumber' id='noNumber' component={CheckboxFormField} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={10}>
          <Field label='Bairro' name='neighborhood' id='neighborhood' component={TextFormField} />
        </Col>
        <Col xs={24} sm={8}>
          <Field label='Complemento' name='additionalInfo' id='additionalInfo' component={TextFormField} />
        </Col>
        <Col xs={24} sm={6}>
          <Field label='Telefone' name='phone' id='phone' mask={'phone'} component={TextFormField} />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Field label='CPF' name='cpf' id='cpf' mask='cpf' component={TextFormField} />
        </Col>
      </Row>
    </>
  );
};

export default Address;
